import { authHeaders } from '@/auth/tokenInterceptor';
import { connectionId } from '@/lib/realtime';

// Typed error so views can distinguish 401/403/409 from generic failures (UI surfaces 409 as
// "someone else saved this file" and 403 as "you're not in the admin group").
export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T | null> {
  const auth = await authHeaders();
  // When the SignalR handshake has completed, stamp the request with our hub connection id so
  // the broadcast that fires from this write echoes back with a matching OriginConnectionId —
  // the SPA then skips its own echo. The header is absent for the brief sub-second window
  // before the handshake lands; in that case we'll get a phantom modal at worst.
  const headers: Record<string, string> = { ...auth };
  if (connectionId.value) {
    headers['X-Realtime-Connection-Id'] = connectionId.value;
  }
  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    init.headers = { ...headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const response = await fetch(path, init);

  if (response.status === 204) return null;

  const text = await response.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }

  if (!response.ok) {
    throw new ApiError(response.status, `${response.status} ${response.statusText}`, parsed);
  }
  return parsed as T;
}

export const apiGet = <T>(path: string) => request<T>('GET', path) as Promise<T>;
export const apiPost = <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}) as Promise<T>;
export const apiPut = <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}) as Promise<T | null>;
export const apiPatch = <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}) as Promise<T>;
export const apiDelete = (path: string) => request<void>('DELETE', path) as Promise<null>;
